"use client";

import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  Avatar,
  MenuItem,
  IconButton,
  Menu,
  Chip,
} from "@mui/material";
import { useState, useEffect } from "react";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useTranslation } from "react-i18next";
// Activity item component
const ActivityItem = ({ icon, color, title, time, link }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mr: 2,
        }}
      >
        <Avatar
          sx={{
            bgcolor: color,
            width: 40,
            height: 40,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {icon}
        </Avatar>
        <Box
          sx={{
            width: 2,
            height: 30,
            bgcolor: "#e0e0e0",
            mt: 1,
            mb: 1,
          }}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: "medium",
            color: "text.primary",
            textTransform: 'capitalize'
          }}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        {/* <Typography variant="caption" display={"block"} color="text.secondary">
          {time}
        </Typography> */}
      </Box>
    </Box>
  );
};

const RecentActivityTimeline = ({
  recentActivities,
  loading,
  lightMode,
  timeFilter,
  onTimeFilterChange,
  userFilter,
  onUserFilterChange,
}) => {
  const theme = useTheme();
  const [uniqueUsers, setUniqueUsers] = useState([]);

  // Menu states
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [timeMenuAnchorEl, setTimeMenuAnchorEl] = useState(null);

  const userMenuOpen = Boolean(userMenuAnchorEl);
  const timeMenuOpen = Boolean(timeMenuAnchorEl);

  const { t } = useTranslation();
  // Format time for display
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get icon for activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case "invoice":
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h7.5" />
            <path d="M16 2v4" />
            <path d="M8 2v4" />
            <path d="M3 10h18" />
            <path d="M18 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
            <path d="m22 22-1.5-1.5" />
          </svg>
        );
      case "invoice_overdue":
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case "payment_overdue":
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <circle cx="12" cy="12" r="2" />
            <path d="M6 12h.01M18 12h.01" />
            <path d="m14 16 2 2 4-4" opacity="0.4" />
          </svg>
        );
      case "quote":
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <line x1="10" y1="9" x2="8" y2="9" />
          </svg>
        );
      default:
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <path d="M8 14h.01" />
            <path d="M12 14h.01" />
            <path d="M16 14h.01" />
            <path d="M8 18h.01" />
            <path d="M12 18h.01" />
            <path d="M16 18h.01" />
          </svg>
        );
    }
  };

  // Para formatı
  const paraFormat = (number) => {
    const formatter = new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    });
    return formatter.format(number);
  };

  // Extract unique users from activities
  useEffect(() => {
    if (recentActivities) {
      const users = new Set();

      // Add users from recent invoices
      if (recentActivities.recent_invoices) {
        recentActivities.recent_invoices.forEach((item) => {
          if (item.student_name) users.add(item.student_name);
        });
      }

      // Add users from overdue invoices
      if (recentActivities.recent_invoices_overdue) {
        recentActivities.recent_invoices_overdue.forEach((item) => {
          if (item.student_name) users.add(item.student_name);
        });
      }

      // Add users from overdue payments
      if (recentActivities.recent_payments_overdue) {
        recentActivities.recent_payments_overdue.forEach((item) => {
          if (item.student_name) users.add(item.student_name);
        });
      }

      // Add users from quotes
      if (recentActivities.recent_quotes) {
        recentActivities.recent_quotes.forEach((item) => {
          if (item.student_name) users.add(item.student_name);
        });
      }

      setUniqueUsers(Array.from(users));
    }
  }, [recentActivities]);

  // Tüm verileri birleştirme ve sıralama
  const getAllActivities = () => {
    if (!recentActivities) return [];

    const allActivities = [];

    // Recent Invoices
    if (recentActivities.recent_invoices) {
      recentActivities.recent_invoices.forEach((item) => {
        allActivities.push({
          id: `invoice_${item.invoice_id}`,
          type: "invoice",
          title: `<strong>${item.student_name
            }</strong> ${t("receivedANewInvoice")} ${paraFormat(item.price)}`,
          date: new Date(item.created_at),
          status: item.status,
          amount: item.price,
          icon: "invoice",
          color: "#2196f3",
          student_name: item.student_name,
        });
      });
    }

    // Recent Invoices Overdue
    if (recentActivities.recent_invoices_overdue) {
      recentActivities.recent_invoices_overdue.forEach((item) => {
        allActivities.push({
          id: `invoice_overdue_${item.invoice_id}`,
          type: "invoice_overdue",
          title: `<strong>${item.student_name}</strong>'s ${t("invoiceIsOverdueBy")} ${item.overdue_days} ${t("day")}`,
          date: new Date(item.due_date),
          status: item.status,
          amount: item.outstanding,
          icon: "overdue",
          color: "#f44336",
          student_name: item.student_name,
        });
      });
    }

    // Recent Payments Overdue
    if (recentActivities.recent_payments_overdue) {
      recentActivities.recent_payments_overdue.forEach((item) => {
        allActivities.push({
          id: `payment_overdue_${item.payment_id}`,
          type: "payment_overdue",
          title: `<strong>${item.student_name}</strong>'s ${t("paymentIsOverdueBy")} ${item.overdue_days} ${t("day")}`,
          date: new Date(item.due_date),
          amount: item.outstanding_amount,
          icon: "payment",
          color: "#ff9800",
          student_name: item.student_name,
        });
      });
    }

    // Recent Quotes
    if (recentActivities.recent_quotes) {
      recentActivities.recent_quotes.forEach((item) => {
        allActivities.push({
          id: `quote_${item.quote_id}`,
          type: "quote",
          title: `<strong>${item.student_name}</strong> ${t("receivedANewQuote")}`,
          date: new Date(item.created_at),
          status: item.quote_status,
          icon: "quote",
          color: "#4caf50",
          student_name: item.student_name,
        });
      });
    }

    // Sort by date (newest first)
    return allActivities.sort((a, b) => b.date - a.date);
  };

  // Filter activities by time and user
  const getFilteredActivities = () => {
    const allActivities = getAllActivities();
    let filteredActivities = allActivities;

    // Apply time filter
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    if (timeFilter === "today") {
      filteredActivities = filteredActivities.filter((activity) => {
        const activityDate = new Date(activity.date);
        activityDate.setHours(0, 0, 0, 0);
        return activityDate.getTime() === today.getTime();
      });
    } else if (timeFilter === "yesterday") {
      filteredActivities = filteredActivities.filter((activity) => {
        const activityDate = new Date(activity.date);
        activityDate.setHours(0, 0, 0, 0);
        return activityDate.getTime() === yesterday.getTime();
      });
    } else if (timeFilter === "week") {
      filteredActivities = filteredActivities.filter((activity) => {
        return activity.date >= lastWeek;
      });
    }

    // Apply user filter
    if (userFilter !== "all") {
      filteredActivities = filteredActivities.filter(
        (activity) => activity.student_name === userFilter
      );
    }

    return filteredActivities;
  };

  // Group activities by date
  const groupActivitiesByDate = () => {
    const activities = getFilteredActivities();
    const grouped = {};

    activities.forEach((activity) => {
      const date = new Date(activity.date);
      const dateStr = date.toISOString().split("T")[0];

      if (!grouped[dateStr]) {
        grouped[dateStr] = [];
      }

      grouped[dateStr].push(activity);
    });

    return grouped;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (
      date.toISOString().split("T")[0] === today.toISOString().split("T")[0]
    ) {
      return "Today";
    } else if (
      date.toISOString().split("T")[0] === yesterday.toISOString().split("T")[0]
    ) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
  };

  // Handle user filter change
  const handleUserFilterChange = (newValue) => {
    onUserFilterChange(newValue); // Ana bileşene değişikliği iletiyoruz
    setUserMenuAnchorEl(null);
  };

  // Handle time filter change
  const handleTimeFilterChange = (newValue) => {
    onTimeFilterChange(newValue);
    setTimeMenuAnchorEl(null);
  };

  // Get time filter label
  const getTimeFilterLabel = () => {
    switch (timeFilter) {
      case "today":
        return t("today");
      case "yesterday":
        return t("yesterday");
      case "week":
        return t("thisWeek");
      case "all":
      default:
        return t("allTime");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 2,
        maxHeight: 600,
        borderRight: `1px solid ${lightMode ? `#e9ecee` : `#333e47`}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "space-between",
          borderBottom: `1px dashed ${lightMode ? `#e9ecee` : `#333e47`}`,
        }}
      >
        <Typography
          variant="h5"
          color={lightMode ? theme.palette.customRed?.orange : "#ffffff"}
          sx={{
            fontWeight: "700",
            mb: 2,
            borderBottom: `1px dashed ${lightMode ? `#e9ecee` : `#333e47`}`,
            pb: 1,
            textTransform: 'uppercase'
          }}
        >
          {t("recentActivities")}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              sx={{
                borderRadius: 3,
              }}
              onClick={(e) => setUserMenuAnchorEl(e.currentTarget)}
              display={"flex"}
              gap={0}
              alignItems={"center"}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid",
                  height: 26,
                  width: 26,
                  borderRadius: "100%",
                  borderColor: "divider",
                  bgcolor: lightMode ? "white" : "background.paper",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#919EAB"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </Box>
              <UnfoldMoreIcon
                fontSize="16"
                sx={{
                  color: "#919EAB",
                }}
              />
            </IconButton>

            <IconButton
              sx={{
                borderRadius: 3,
              }}
              onClick={(e) => setTimeMenuAnchorEl(e.currentTarget)}
              display={"flex"}
              gap={0}
              alignItems={"center"}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid",
                  height: 26,
                  width: 26,
                  borderRadius: "100%",
                  borderColor: "divider",
                  bgcolor: lightMode ? "white" : "background.paper",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#919EAB"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </Box>
              <UnfoldMoreIcon
                fontSize="16"
                sx={{
                  color: "#919EAB",
                }}
              />
            </IconButton>
          </Box>
        </Box>

        {/* Filter chips */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Chip
            size="small"
            sx={{
              textTransform: 'capitalize'
            }}
            label={userFilter === "all" ? t("allStudents") : userFilter}
            color={userFilter === "all" ? "default" : "primary"}
            onDelete={
              userFilter === "all"
                ? undefined
                : () => handleUserFilterChange("all")
            }
          />
          <Chip
            size="small"
            label={getTimeFilterLabel()}
            sx={{
              textTransform: 'capitalize'
            }}
            color={timeFilter === "all" ? "default" : "primary"}
            onDelete={
              timeFilter === "all"
                ? undefined
                : () => handleTimeFilterChange("all")
            }
          />
        </Box>

        {/* User filter menu */}
        <Menu
          anchorEl={userMenuAnchorEl}
          open={userMenuOpen}
          onClose={() => setUserMenuAnchorEl(null)}
        >
          <MenuItem
            sx={{
              fontSize: "14px",
              textTransform: 'capitalize'
            }}
            value="all"
            selected={userFilter === "all"}
            onClick={() => handleUserFilterChange("all")}
          >
            {t("allStudents")}
          </MenuItem>
          {uniqueUsers.map((user) => (
            <MenuItem
              sx={{
                fontSize: "14px",
              }}
              key={user}
              value={user}
              selected={userFilter === user}
              onClick={() => handleUserFilterChange(user)}
            >
              {user}
            </MenuItem>
          ))}
        </Menu>

        {/* Time filter menu */}
        <Menu
          anchorEl={timeMenuAnchorEl}
          open={timeMenuOpen}
          onClose={() => setTimeMenuAnchorEl(null)}
        >
          <MenuItem
            sx={{
              fontSize: "14px",
              textTransform: 'capitalize'
            }}
            value="all"
            selected={timeFilter === "all"}
            onClick={() => handleTimeFilterChange("all")}
          >
            {t("allTime")}
          </MenuItem>
          <MenuItem
            sx={{
              fontSize: "14px",
              textTransform: 'capitalize'
            }}
            value="today"
            selected={timeFilter === "today"}
            onClick={() => handleTimeFilterChange("today")}
          >
            {t("today")}
          </MenuItem>
          <MenuItem
            sx={{
              fontSize: "14px",
              textTransform: 'capitalize'
            }}
            value="yesterday"
            selected={timeFilter === "yesterday"}
            onClick={() => handleTimeFilterChange("yesterday")}
          >
            {t("yesterday")}
          </MenuItem>
          <MenuItem
            sx={{
              fontSize: "14px",
              textTransform: 'capitalize'
            }}
            value="week"
            selected={timeFilter === "week"}
            onClick={() => handleTimeFilterChange("week")}
          >
            {t("thisWeek")}
          </MenuItem>
        </Menu>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            overflow: "auto",
            pt: 2,
          }}
        >
          {Object.entries(groupActivitiesByDate()).length > 0 ? (
            Object.entries(groupActivitiesByDate()).map(
              ([date, activities]) => (
                <Box
                  key={date}
                  sx={{
                    mb: 2,
                    pb: 2,
                    borderBottom: `1px dashed ${lightMode ? `#e9ecee` : `#333e47`
                      }`,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 2,
                      color: "text.secondary",
                      fontWeight: "medium",
                    }}
                  >
                    {formatDate(date)}
                  </Typography>

                  {activities.map((activity) => (
                    <ActivityItem
                      key={activity.id}
                      icon={getActivityIcon(activity.type)}
                      color={activity.color}
                      title={activity.title}
                      time={formatTime(activity.date)}
                      link="#"
                    />
                  ))}
                </Box>
              )
            )
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", py: 4 }}>
              {t("noActivitiesFoundForTheSelectedFilters")}
            </Typography>
          )}

          {/* <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Typography
              variant="body2"
              color="primary"
              sx={{
                cursor: "pointer",
                fontWeight: "medium",
              }}
            >
              View All
            </Typography>
          </Box> */}
        </Box>
      )}
    </Box>
  );
};

export default RecentActivityTimeline;
